import { getSearchAppName, calculatePercentChange } from 'src/utils/app';
import axios from 'axios';
import {
  CategoryType,
  ContentMetricType,
  GetContentResponse,
  GetScoreForContentMetricByCategoryTypeRequest,
  GetScoreForContentMetricByWeekResponse,
  GetScoreForContentMetricRequest,
  GetVoiceAndToneCurrentAndTargetVectorsResponse,
  GetWeightedKeywordsAndPhrasesForProductResponse,
  KeywordPerformanceScores,
  TimePeriodRange,
  VoiceAndToneMatchType,
  ContentUtilizationFilters,
  GetVariationsMetricsByCategoryRequest,
  VariationsDetailGridMetricType,
  VariationsMetricsByCategoryRequestType
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';

import { useRetailerId, useTimePeriods } from 'src/utils/Hooks/reduxSelectors';
import { ComparisonPeriod, useAppSelector, useComparisonPeriod, useWeekRange, WeekRange } from 'src/utils/Hooks';
import {
  ChartDataAdapter,
  Product,
  TableGridData
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';
import {
  ContentScoresWithMetadata,
  KeyMetricsDataAdapter
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/KeyMetricsAdapter';
import {
  ContentScoresByWeekAdapter,
  TableGridAdapter,
  VoiceAndToneMatchAnalysisAdapter
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/TableGridAdapter';
import { TimeSeriesData } from './classes/TimeSeriesData';
import { ContentSplineChartData } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/types';
import { getFirstWeekIdOfYear, getPreviousWeekId, getPreviousYearWeekId, yearPartOfWeekId } from 'src/utils/dateUtils';
import {
  RecordPropertyType,
  WeekContentMetricType
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/helpers/contentKeywordUtils';
import { KeywordsPerformanceSummaryAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/KeywordsPerformanceSummaryAdapter';

import { ProductLevelKeywordsDetailsAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/ProductLevelKeywordsDetailsAdapter';
import { ProductLevelVoiceAndToneAnalysisAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/ProductLevelVoiceAndToneAnalysisAdapter';
import { ProductLevelContentAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/ProductLevelContentAdapter';
import {
  useBaseContentRequestBuilder,
  useContentMetricsForProductRequestBuilder,
  useWeightedKeywordsAndPhrasesForProductRequestBuilder,
  useKeywordOpportunitiesRequestBuilder,
  useVariationsRequestBuilder
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useBaseContentRequestBuilder';
import { TimeSeriesAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/classes/TimeSeriesAdapter';
import {
  CategoryScoreAdapter,
  VoiceAndToneProductCountAdapter
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/CategoryScoreAdapter';
import { ProductContentUtilizationAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/ProductContentUtilizationAdapter';
import { ProductContentAccuracyAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/ProductContentAccuracyAdapter';
import { ContentServerProxyImpl } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/contentServerProxy';
import { KeywordProductivityType } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/ContentRequests/ContentRequestBuilder';
import { VariationsPageSizeAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/VariationsPageSizeAdapter';
import { VariationsByCategoryAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/VariationsByCategoryAdapter';
import { VariationsFilter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/helpers/contentVariationUtils';
import { VariationsGaugeAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/VariationsGaugeAdapter';
import { VariationsSplineAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/VariationsSplineAdapter';
import { DEFAULT_KEYWORD_PRODUCTIVITY_THRESHOLD } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/utils';
import { DEFAULT_KEYWORD_POPULARITY_THRESHOLD } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/utils';
import { DEFAULT_KEYWORD_USAGE_THRESHOLD } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/utils';
import { PAGINATION_CONSTANTS } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/constants';
import { useLastWeek } from 'src/hooks/useLastWeek';

/**
 * Higher-order function that wraps async operations with error handling
 * @param operation - The async operation to execute
 * @param defaultValue - The fallback value to return if the operation fails
 * @param errorMessage - The message to log if an error occurs
 * @returns Promise resolving to either the operation result or the default value
 */
const withErrorHandling = async <T>(operation: () => Promise<T>, defaultValue: T, errorMessage: string): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    console.error(errorMessage, error);
    return defaultValue;
  }
};

export const useContentService = () => {
  const requestBuilder = useBaseContentRequestBuilder();
  const productRequestBuilder = useContentMetricsForProductRequestBuilder();
  const weightedKeywordsAndPhrasesRequestBuilder = useWeightedKeywordsAndPhrasesForProductRequestBuilder();
  const keywordOpportunitiesRequestBuilder = useKeywordOpportunitiesRequestBuilder();
  const variationsRequestBuilder = useVariationsRequestBuilder();
  const { mainTimePeriod: lastWeekMainPeriod, comparisonTimePeriod: lastWeekComparisonPeriod } = useLastWeek();

  const client = new ContentServerProxyImpl();
  const retailerId = useRetailerId();
  const { mainTimePeriod: defaultMainTimePeriod, comparisonTimePeriod: defaultComparisonTimePeriod } = useTimePeriods();
  const comparisonPeriod = useComparisonPeriod();

  const weekRange = useWeekRange();

  const categoryNames = useAppSelector((state) =>
    state.categories.reduce((acc, category) => {
      acc[category.id] = category.displayName;
      return acc;
    }, {} as Record<number, string>)
  );
  const subcategoryNames = useAppSelector((state) =>
    state.subCategories.reduce((acc, subcategory) => {
      acc[subcategory.id] = subcategory.displayName;
      return acc;
    }, {} as Record<number, string>)
  );
  const brandNames = useAppSelector((state) =>
    state.brandsFollowing.reduce((acc, brand) => {
      // @ts-ignore
      acc[brand.brandId] = brand.displayName;
      return acc;
    }, {} as Record<number, string>)
  );

  const getKeyMetricsInner = async (
    metricType: ContentMetricType,
    timePeriod: TimePeriodRange,
    filters?: ContentUtilizationFilters
  ) => {
    return withErrorHandling(
      async () => {
        const builder = requestBuilder.clone().setStartWeekId(timePeriod.startWeek).setEndWeekId(timePeriod.endWeek);

        if (filters) {
          builder.setFilters(filters);
        }

        switch (metricType) {
          case ContentMetricType.ContentScore:
            return (await client.getContentScoreSummary(builder.build())).scores;
          case ContentMetricType.ContentUtilizationScore:
            return (await client.getContentUtilizationSummary(builder.build())).contentUtilizationScores;
          case ContentMetricType.ContentAccuracyScore:
            return (await client.getContentAccuracySummary(builder.build())).contentAccuracyScores;
          default:
            throw new Error(`Unsupported metric type: ${metricType}`);
        }
      },
      {} as ContentScoresWithMetadata,
      `Error fetching content utilization key metrics for time period ${timePeriod}`
    );
  };

  /**
   * Fetches and compares content utilization metrics between two time periods.
   * @param mainTimePeriod - The primary time period to analyze, defaults to current selected period
   * @param comparisonTimePeriod - The secondary time period for comparison, defaults to current comparison period
   * @returns Promise resolving to a KeyMetricsDataAdapter containing the metrics data for both periods
   */
  const getContentScoresKeyMetrics = async (
    metricType: ContentMetricType,
    mainTimePeriod: TimePeriodRange = defaultMainTimePeriod,
    comparisonTimePeriod: TimePeriodRange = defaultComparisonTimePeriod,
    filters?: ContentUtilizationFilters
  ) => {
    const [mainMetrics, comparisonMetrics] = await Promise.all([
      getKeyMetricsInner(metricType, mainTimePeriod, filters),
      getKeyMetricsInner(metricType, comparisonTimePeriod, filters)
    ]);

    return new KeyMetricsDataAdapter(mainMetrics, comparisonMetrics, metricType);
  };

  /**
   * Fetches scores by week for a given content metric request
   * @param contentMetricType - The type of content metric to analyze
   * @param timePeriod - The time period range containing start and end weeks to fetch data for
   * @returns Promise resolving to an array of [timestamp, score] pairs
   */
  const getScoresByWeek = async (
    request: GetScoreForContentMetricRequest
  ): Promise<GetScoreForContentMetricByWeekResponse> => {
    return withErrorHandling(
      async () => {
        const response = await client.getScoreForContentMetricByWeek(request);
        return response;
      },

      { records: [] },
      'Error fetching scores by week'
    );
  };

  /**
   * Retrieves content scores over time for both main and comparison time periods
   */
  const getContentScoresByWeek = async ({
    contentMetricType,
    mainTimePeriod = defaultMainTimePeriod,
    comparisonTimePeriod = defaultComparisonTimePeriod,
    voiceAndToneMatchTypes,
    recordProperty = WeekContentMetricType.score,
    keywordProductivityType // default to undefined
  }: {
    contentMetricType: ContentMetricType;
    mainTimePeriod: TimePeriodRange;
    comparisonTimePeriod: TimePeriodRange;
    voiceAndToneMatchTypes?: VoiceAndToneMatchType[];
    recordProperty?: RecordPropertyType;
    keywordProductivityType?: KeywordProductivityType | undefined;
  }): Promise<ContentSplineChartData> => {
    return withErrorHandling(
      async () => {
        const request = requestBuilder
          .clone()
          .setContentMetricType(contentMetricType)
          .setVoiceAndToneMatchTypes(voiceAndToneMatchTypes)
          .setKeywordProductivityType(keywordProductivityType);

        const isLastWeekWithPriorYear =
          weekRange === WeekRange.LAST_WEEK && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR;

        const mainPeriodStart = isLastWeekWithPriorYear
          ? getFirstWeekIdOfYear(yearPartOfWeekId(defaultMainTimePeriod.endWeek))
          : mainTimePeriod.startWeek;
        const mainTimePeriodReqBuilder = request
          .clone()
          .setStartWeekId(mainPeriodStart)
          .setEndWeekId(mainTimePeriod.endWeek);

        const comparisonPeriodStart = isLastWeekWithPriorYear
          ? getPreviousYearWeekId(mainPeriodStart)
          : comparisonTimePeriod.startWeek;

        const comparisonPeriodEnd =
          isLastWeekWithPriorYear || (weekRange === WeekRange.YTD && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR)
            ? getPreviousWeekId(mainPeriodStart)
            : comparisonTimePeriod.endWeek;

        const comparisonTimePeriodReqBuilder = request
          .clone()
          .setStartWeekId(comparisonPeriodStart)
          .setEndWeekId(comparisonPeriodEnd);

        const [primaryScores, secondaryScores] = await Promise.all([
          getScoresByWeek(mainTimePeriodReqBuilder.build() as GetScoreForContentMetricRequest),
          getScoresByWeek(comparisonTimePeriodReqBuilder.build() as GetScoreForContentMetricRequest)
        ]);

        // If it's last week with prior year comparison, we only want to use
        // the last week's data for calculating averages
        const primarySeries = new TimeSeriesData([]).createWeekRecords(
          primaryScores.records,
          (record) => record[recordProperty] // default to score
        );

        const comparisonSeries = new TimeSeriesData([]).createWeekRecords(
          secondaryScores.records,
          (record) => record[recordProperty] // default to score
        );

        const { startWeek, endWeek } = mainTimePeriod;
        const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = comparisonTimePeriod;

        const primaryAverage = primarySeries.compute('average', {
          range: [startWeek, endWeek]
        });

        const comparisonAverage = comparisonSeries.compute('average', {
          range: [comparisonStartWeek, comparisonEndWeek]
        });

        const percentChange = calculatePercentChange(primaryAverage, comparisonAverage);

        // Calculate averageKeywordCount when recordProperty is 'averageSearchVolume'
        let averageKeywordCount = 0;
        if (recordProperty === WeekContentMetricType.averageSearchVolume) {
          const primaryKeywordSeries = new TimeSeriesData([]).createWeekRecords(
            primaryScores.records,
            (record) => record.averageCountOfKeywords
          );

          averageKeywordCount = primaryKeywordSeries.compute('average', {
            range: [startWeek, endWeek]
          });
        }

        const timeSeriesAdapter = new TimeSeriesAdapter(primarySeries, comparisonSeries, comparisonPeriod);
        const chartData = timeSeriesAdapter.transform();

        return {
          chartData,
          legendData: {
            primaryLegendMetric: primaryAverage,
            secondaryLegendMetric: comparisonAverage,
            percentChange
          },
          averageKeywordCount
        };
      },

      {
        chartData: { primaryMetrics: [], secondaryMetrics: [] },
        legendData: { primaryLegendMetric: 0, secondaryLegendMetric: 0, percentChange: 0 },
        averageKeywordCount: 0
      },
      'Error fetching content scores by week'
    );
  };

  const getProducts = async (stacklineSkus: string[]): Promise<Product[]> => {
    return withErrorHandling(
      () =>
        axios
          .post<Product[]>('/api/beacon/GetEntityMetadataInBulk', {
            entityType: 'product',
            entityIds: stacklineSkus,
            retailerId
          })
          .then((response) => response.data),
      [],
      `Error fetching products for Stackline SKUs ${stacklineSkus.join(', ')}`
    );
  };

  /**
   * Creates a mapping of Stackline SKUs to their corresponding Product objects
   * @param productSkus - Array of Stackline SKU strings to fetch product data for
   * @returns Promise resolving to a Record mapping SKUs to Product objects
   */
  const getProductsMap = async (productSkus: string[]): Promise<Record<string, Product>> => {
    return withErrorHandling(
      async () => {
        const products = await getProducts(productSkus);
        return products.reduce((acc, product) => {
          acc[product.stacklineSku] = product;
          return acc;
        }, {} as Record<string, Product>);
      },
      {},
      'Error fetching products map'
    );
  };

  /**
   * Internal helper function to fetch content scores for all products for a specific time period
   * @param contentMetricType - The type of content metric to analyze (e.g. ContentUtilizationScore)
   * @param timePeriod - Time range to fetch scores for, containing start and end week IDs
   * @returns Promise resolving to an array of content metric score records by product
   */
  const getContentScoresByProductInner = async (
    contentMetricType: ContentMetricType,
    voiceAndToneMatchTypes: VoiceAndToneMatchType[],
    timePeriod: TimePeriodRange,
    filters?: ContentUtilizationFilters
  ) => {
    return withErrorHandling(
      async () => {
        const req = requestBuilder
          .clone()
          .setContentMetricType(contentMetricType)
          .setVoiceAndToneMatchTypes(voiceAndToneMatchTypes)
          .setMainTimePeriod(timePeriod)
          .setFilters(filters)
          .build() as GetScoreForContentMetricRequest & ContentUtilizationFilters;
        const response = await client.getScoreForContentMetricByProduct(req);
        return response.records;
      },
      [],
      `Error fetching ${contentMetricType} scores by product for time period ${timePeriod}`
    );
  };

  /**
   * Retrieves content scores for all products across two time periods
   * @param contentMetricType - The type of content metric to analyze (e.g. ContentUtilizationScore)
   * @param mainTimePeriod - Primary time range to fetch scores for, defaults to the default main time period
   * @param comparisonTimePeriod - Secondary time range to compare against, defaults to the default comparison time period
   * @param page - Page number for pagination support
   * @param pageSize - Number of items per page for pagination
   * @returns Promise resolving to a TableGridAdapter that can transform the data to be rendered in a table grid
   */
  const getContentScoresByProduct = async (
    contentMetricType: ContentMetricType,
    mainTimePeriod: TimePeriodRange = defaultMainTimePeriod,
    comparisonTimePeriod: TimePeriodRange = defaultComparisonTimePeriod,
    voiceAndToneMatchTypes?: VoiceAndToneMatchType[],
    filters?: ContentUtilizationFilters,
    page?: number,
    pageSize = PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE
  ): Promise<ChartDataAdapter<TableGridData>> => {
    const [mainProductRecords, comparisonProductRecords] = await Promise.all([
      getContentScoresByProductInner(contentMetricType, voiceAndToneMatchTypes, mainTimePeriod, filters),
      getContentScoresByProductInner(contentMetricType, voiceAndToneMatchTypes, comparisonTimePeriod)
    ]);

    // Handle pagination if page is provided
    let paginatedMainProductRecords = mainProductRecords;
    let paginatedComparisonProductRecords = comparisonProductRecords;

    if (page !== undefined) {
      const currentPage = typeof page === 'number' ? page : 0;
      const startIndex = currentPage * pageSize;
      const endIndex = startIndex + pageSize + 1; // Fetch one extra item to check if there's a next page

      paginatedMainProductRecords = mainProductRecords.slice(startIndex, endIndex);
      paginatedComparisonProductRecords = comparisonProductRecords.slice(startIndex, endIndex);
    }
    const isPaginated = page !== undefined;

    const products = await getProductsMap(
      paginatedMainProductRecords.slice(0, isPaginated ? pageSize : undefined).map((record) => record.stacklineSku)
    );

    if (contentMetricType === ContentMetricType.VoiceAndToneMatchAnalysis) {
      return new VoiceAndToneMatchAnalysisAdapter(
        paginatedMainProductRecords,
        paginatedComparisonProductRecords,
        products,
        isPaginated,
        pageSize
      );
    }

    return new TableGridAdapter(
      paginatedMainProductRecords,
      paginatedComparisonProductRecords,
      products,
      isPaginated,
      pageSize
    );
  };

  const getContentScoreByCategoryInner = async (
    contentMetricType: ContentMetricType,
    categoryType: CategoryType,
    timePeriod: TimePeriodRange,
    voiceAndToneMatchTypes?: VoiceAndToneMatchType[]
  ) => {
    return withErrorHandling(
      async () => {
        const req = requestBuilder
          .clone()
          .setContentMetricType(contentMetricType)
          .setCategoryType(categoryType)
          .setVoiceAndToneMatchTypes(voiceAndToneMatchTypes)
          .setMainTimePeriod(timePeriod)
          .build();

        const response = await client.getScoreForContentMetricByCategoryType(
          req as GetScoreForContentMetricByCategoryTypeRequest
        );
        return response.records;
      },
      [],
      `Error fetching ${contentMetricType} scores by ${categoryType} for time period ${timePeriod}`
    );
  };

  const getBeaconBasicContentScore = async (groupByField: string, timePeriod: TimePeriodRange) => {
    const builder = new AdvancedSearchRequestBuilder(
      'beacon-content-score',
      'beacon-new-content-metrics'
    ).setAggregation({
      groupByFieldName: groupByField as any,
      aggregationFields: [
        {
          aggregateByFieldDisplayName: 'Weighted Content Score',
          aggregateByFieldName: 'weightedContentScoreByUnits',
          function: 'sum'
        },
        {
          aggregateByFieldDisplayName: 'Content Utilization',
          aggregateByFieldName: 'contentScore' as any,
          function: 'computed',
          aggregateByFormula:
            '((unitsSoldForContentScore_sum > 0) ? ((weightedContentScoreByUnits_sum / unitsSoldForContentScore_sum)) : (0))'
        },
        {
          aggregateByFieldDisplayName: 'Units Sold for Content Score',
          aggregateByFieldName: 'unitsSoldForContentScore' as any,
          function: 'sum'
        }
      ]
    });

    return withErrorHandling(
      async () => {
        const response = await axios.post(`/api/${getSearchAppName()}/AdvancedSearch?helloworld`, [builder.build()]);
        return response.data;
      },
      [],
      `Error fetching for time period ${timePeriod}`
    );
  };

  /**
   * Retrieves content scores grouped by category type
   * @param contentMetricType - The type of content metric to analyze
   * @param categoryType - The category classification to group by
   * @returns Promise containing an array of category scores
   */
  const getBeaconContentScoreByCategory = async (
    groupBy: string,
    categoryType: CategoryType,
    mainTimePeriod: TimePeriodRange = defaultMainTimePeriod,
    comparisonTimePeriod: TimePeriodRange = defaultComparisonTimePeriod
  ) => {
    const [mainPeriodResponse, comparisonPeriodResponse] = await Promise.all([
      getBeaconBasicContentScore(groupBy, mainTimePeriod),
      getBeaconBasicContentScore(groupBy, comparisonTimePeriod)
    ]);

    return new CategoryScoreAdapter(categoryType, [mainPeriodResponse, comparisonPeriodResponse], {
      category: categoryNames,
      subcategory: subcategoryNames,
      brand: brandNames
    });
  };

  /**
   * Retrieves content scores grouped by category type
   * @param contentMetricType - The type of content metric to analyze
   * @param categoryType - The category classification to group by
   * @returns Promise containing an array of category scores
   */
  const getContentScoreByCategory = async (
    contentMetricType: ContentMetricType,
    categoryType: CategoryType,
    mainTimePeriod: TimePeriodRange = lastWeekMainPeriod,
    comparisonTimePeriod: TimePeriodRange = lastWeekComparisonPeriod,
    voiceAndToneMatchTypes?: VoiceAndToneMatchType[]
  ) => {
    const [mainPeriodResponse, comparisonPeriodResponse] = await Promise.all([
      getContentScoreByCategoryInner(contentMetricType, categoryType, comparisonTimePeriod, voiceAndToneMatchTypes),
      getContentScoreByCategoryInner(contentMetricType, categoryType, mainTimePeriod, voiceAndToneMatchTypes)
    ]);

    return new CategoryScoreAdapter(categoryType, [mainPeriodResponse, comparisonPeriodResponse], {
      category: categoryNames,
      subcategory: subcategoryNames,
      brand: brandNames
    });
  };

  // TODO(tyan): fetch data from api given stacklineSku and time period
  const getProductLevelContentUtilizationDetails = async () => {
    return withErrorHandling(
      async () => {
        const response = await client.getContentUtilizationDetailMetricsForProduct(productRequestBuilder.build());
        return new ProductContentUtilizationAdapter(response.records);
      },
      new ProductContentUtilizationAdapter([]),
      'Error fetching product content utilization detail metrics'
    );
  };

  const getProductLevelContentAccuracyDetails = async () => {
    return withErrorHandling(
      async () => {
        const response = await client.getContentAccuracyDetailMetricsForProduct(productRequestBuilder.build());
        return new ProductContentAccuracyAdapter(response.records);
      },
      new ProductContentAccuracyAdapter([]),
      'Error fetching product content accuracy detail metrics'
    );
  };

  const getKeywordPerformanceScoreSummaryInner = async (timePeriod: TimePeriodRange) => {
    const filters = {
      filterTitleInaccuracies: false,
      filterBulletPointsInaccuracies: false,
      filterImageInaccuracies: false,
      filterDescriptionInaccuracies: false,
      filterVideoInaccuracies: false,
      minimumRequiredKeywordUsageScore: DEFAULT_KEYWORD_USAGE_THRESHOLD,
      minimumRequiredKeywordPopularityScore: DEFAULT_KEYWORD_POPULARITY_THRESHOLD,
      minimumRequiredKeywordProductivityScore: DEFAULT_KEYWORD_PRODUCTIVITY_THRESHOLD
    };

    return withErrorHandling(
      async () => {
        const response = await client.getKeywordPerformanceScoreSummary(
          requestBuilder.clone().setMainTimePeriod(timePeriod).setFilters(filters).build()
        );
        return response.scores;
      },
      {} as KeywordPerformanceScores,
      `Error fetching keyword performance score summary for time period ${timePeriod} `
    );
  };

  const getKeywordPerformanceScoreSummary = async (
    mainTimePeriod: TimePeriodRange = defaultMainTimePeriod,
    comparisonTimePeriod: TimePeriodRange = defaultComparisonTimePeriod
  ) => {
    const [mainMetrics, comparisonMetrics] = await Promise.all([
      getKeywordPerformanceScoreSummaryInner(mainTimePeriod),
      getKeywordPerformanceScoreSummaryInner(comparisonTimePeriod)
    ]);

    return new KeywordsPerformanceSummaryAdapter(mainMetrics, comparisonMetrics);
  };

  const getProductLevelKeywordPerformanceDetails = async (mainTimePeriod: TimePeriodRange) => {
    return withErrorHandling(
      async () => {
        const detailsResponse = await client.getWeightedKeywordsAndPhrasesForProduct(
          weightedKeywordsAndPhrasesRequestBuilder
            .clone()
            .setContentMetricType(ContentMetricType.KeywordOrganicTraffic)
            .build()
        );

        const summaryResponse = await client.getKeywordPerformanceScoreSummary(
          requestBuilder.setMainTimePeriod(mainTimePeriod).build()
        );

        return new ProductLevelKeywordsDetailsAdapter(detailsResponse, summaryResponse.scores);
      },
      new ProductLevelKeywordsDetailsAdapter(
        {} as GetWeightedKeywordsAndPhrasesForProductResponse,
        {} as KeywordPerformanceScores
      ),
      'Error fetching product level keyword performance details'
    );
  };

  const getContentScoresKeyMetricsByWeekInner = async (timePeriod: TimePeriodRange) => {
    return withErrorHandling(
      async () => {
        const response = await client.getContentScoreExtendedSummaryByWeek(
          requestBuilder.clone().setMainTimePeriod(timePeriod).build()
        );
        return response.records;
      },
      [],
      `Error fetching content scores key metrics by week for time period ${timePeriod} `
    );
  };

  const getContentScoresKeyMetricsByWeek = async (
    mainTimePeriod: TimePeriodRange = defaultMainTimePeriod,
    comparisonTimePeriod: TimePeriodRange = defaultComparisonTimePeriod
  ) => {
    const [mainMetrics, comparisonMetrics] = await Promise.all([
      getContentScoresKeyMetricsByWeekInner(mainTimePeriod),
      getContentScoresKeyMetricsByWeekInner(comparisonTimePeriod)
    ]);

    return new ContentScoresByWeekAdapter(mainMetrics, comparisonMetrics);
  };

  const getProductLevelVoiceAndToneAnalysis = async () => {
    return withErrorHandling(
      async () => {
        const response = await client.getVoiceAndToneCurrentAndTargetVectors(productRequestBuilder.build());
        return new ProductLevelVoiceAndToneAnalysisAdapter(response);
      },
      new ProductLevelVoiceAndToneAnalysisAdapter({} as GetVoiceAndToneCurrentAndTargetVectorsResponse),
      'Error fetching product level voice and tone analysis'
    );
  };

  const getProductContent = async (type: 'current' | 'approved' = 'current') => {
    return withErrorHandling(
      async () => {
        const response =
          type === 'current'
            ? await client.getCurrentContent(productRequestBuilder.build())
            : await client.getApprovedContent(productRequestBuilder.build());

        const currentVoiceAndTone = await client.getVoiceAndToneCurrentAndTargetVectors(productRequestBuilder.build());
        return new ProductLevelContentAdapter(response, currentVoiceAndTone);
      },
      new ProductLevelContentAdapter({} as GetContentResponse, {} as GetVoiceAndToneCurrentAndTargetVectorsResponse),
      `Error fetching ${type} content`
    );
  };

  const getProductCountByVoiceType = async (
    timePeriod: TimePeriodRange,
    voiceAndToneMatchTypes?: VoiceAndToneMatchType[]
  ) => {
    return withErrorHandling(
      async () => {
        const response = await client.getProductCountByVoiceType(
          requestBuilder.clone().setMainTimePeriod(timePeriod).setVoiceAndToneMatchTypes(voiceAndToneMatchTypes).build()
        );
        return response.records;
      },
      [],
      'Error fetching product count by voice type'
    );
  };

  const getProductCountByToneType = async (
    timePeriod: TimePeriodRange,
    voiceAndToneMatchTypes?: VoiceAndToneMatchType[]
  ) => {
    return withErrorHandling(
      async () => {
        const response = await client.getProductCountByToneType(
          requestBuilder.clone().setMainTimePeriod(timePeriod).setVoiceAndToneMatchTypes(voiceAndToneMatchTypes).build()
        );
        return response.records;
      },
      [],
      'Error fetching product count by tone type'
    );
  };

  const getProductCountByVoiceOrTone = async (
    type: 'voice' | 'tone',
    mainTimePeriod: TimePeriodRange,
    comparisonTimePeriod: TimePeriodRange,
    voiceAndToneMatchTypes?: VoiceAndToneMatchType[]
  ) => {
    const getCountByType = type === 'voice' ? getProductCountByVoiceType : getProductCountByToneType;
    const [mainResult, comparisonResult] = await Promise.all([
      getCountByType(mainTimePeriod, voiceAndToneMatchTypes),
      getCountByType(comparisonTimePeriod, voiceAndToneMatchTypes)
    ]);
    return new VoiceAndToneProductCountAdapter(type, [mainResult, comparisonResult]);
  };

  const getKeywordOpportunities = async () => {
    return withErrorHandling(
      async () => {
        const response = await client.getKeywordOpportunities(keywordOpportunitiesRequestBuilder.build());
        return response.data;
      },
      [],
      'Error fetching keyword opportunities'
    );
  };

  const getVariationsOverviewMetricsForGaugeCards = async (
    mainTimePeriod: TimePeriodRange = defaultMainTimePeriod,
    comparisonTimePeriod: TimePeriodRange = defaultComparisonTimePeriod
  ) => {
    const getMetricsForPeriod = async (timePeriod: TimePeriodRange) => {
      return withErrorHandling(
        async () => {
          const response = await client.getVariationsOverviewMetrics(
            variationsRequestBuilder
              .clone()
              .setRequestType('overview')
              .setStartWeekId(timePeriod.endWeek)
              .setEndWeekId(timePeriod.endWeek)
              .build()
          );
          return response.data;
        },
        {
          singleSkuCount: 0,
          variatedSkuCount: 0,
          correctChildCount: 0,
          incorrectChildCount: 0,
          inaccurateParentPages: 0
        },
        `Error fetching variations general metrics for time period ${timePeriod}`
      );
    };

    const getVariationsMetricsForPeriod = async (timePeriod: TimePeriodRange) => {
      return withErrorHandling(
        async () => {
          const response = await client.getVariationsMetrics(
            variationsRequestBuilder
              .clone()
              .setRequestType('trend')
              .setStartWeekId(timePeriod.endWeek)
              .setEndWeekId(timePeriod.endWeek)
              .build()
          );
          return response.data.records;
        },
        [],
        `Error fetching variations metrics for time period ${timePeriod}`
      );
    };

    const [mainMetrics, mainVariationsMetricsRecords, comparisonVariationsMetricsRecords] = await Promise.all([
      getMetricsForPeriod(mainTimePeriod),
      getVariationsMetricsForPeriod(mainTimePeriod),
      getVariationsMetricsForPeriod(comparisonTimePeriod)
    ]);

    return new VariationsGaugeAdapter(mainVariationsMetricsRecords, comparisonVariationsMetricsRecords, mainMetrics);
  };

  const getVariationsMetrics = async (
    mainTimePeriod: TimePeriodRange = defaultMainTimePeriod,
    comparisonTimePeriod: TimePeriodRange = defaultComparisonTimePeriod
  ) => {
    return withErrorHandling(
      async () => {
        const startWeek = Math.min(mainTimePeriod.startWeek, comparisonTimePeriod.startWeek);
        const endWeek = Math.max(mainTimePeriod.endWeek, comparisonTimePeriod.endWeek);

        const response = await client.getVariationsMetrics(
          variationsRequestBuilder
            .clone()
            .setRequestType('trend')
            .setStartWeekId(startWeek)
            .setEndWeekId(endWeek)
            .build()
        );

        return response.data.records;
      },
      [],
      'Error fetching variations metrics'
    );
  };

  const getVariationsMetricsForSplineChart = async (
    filterKey: VariationsFilter,
    mainTimePeriod: TimePeriodRange = defaultMainTimePeriod,
    comparisonTimePeriod: TimePeriodRange = defaultComparisonTimePeriod
  ) => {
    return withErrorHandling(
      async () => {
        const getVariationsMetricsForPeriod = async (timePeriod: TimePeriodRange) => {
          const response = await client.getVariationsMetrics(
            variationsRequestBuilder
              .clone()
              .setRequestType('trend')
              .setStartWeekId(timePeriod.startWeek)
              .setEndWeekId(timePeriod.endWeek)
              .build()
          );
          return response.data.records;
        };

        const isLastWeekWithPriorYear =
          weekRange === WeekRange.LAST_WEEK && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR;

        const mainPeriodStart = isLastWeekWithPriorYear
          ? getFirstWeekIdOfYear(yearPartOfWeekId(defaultMainTimePeriod.endWeek))
          : mainTimePeriod.startWeek;

        const comparisonPeriodStart = isLastWeekWithPriorYear
          ? getPreviousYearWeekId(mainPeriodStart)
          : comparisonTimePeriod.startWeek;

        const comparisonPeriodEnd =
          isLastWeekWithPriorYear || (weekRange === WeekRange.YTD && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR)
            ? getPreviousWeekId(mainPeriodStart)
            : comparisonTimePeriod.endWeek;

        const adjustedMainTimePeriod = {
          startWeek: mainPeriodStart,
          endWeek: mainTimePeriod.endWeek
        };

        const adjustedComparisonTimePeriod = {
          startWeek: comparisonPeriodStart,
          endWeek: comparisonPeriodEnd
        };

        const [mainRecords, comparisonRecords] = await Promise.all([
          getVariationsMetricsForPeriod(adjustedMainTimePeriod),
          getVariationsMetricsForPeriod(adjustedComparisonTimePeriod)
        ]);

        return new VariationsSplineAdapter(mainRecords, comparisonRecords, filterKey, comparisonPeriod, weekRange);
      },
      new VariationsSplineAdapter([], [], filterKey, comparisonPeriod, weekRange),
      `Error fetching variations spline chart data for ${filterKey}`
    );
  };

  const getVariationsByCategory = async (
    requestType: VariationsMetricsByCategoryRequestType,
    categoryType: CategoryType,
    mainTimePeriod: TimePeriodRange = defaultMainTimePeriod,
    comparisonTimePeriod: TimePeriodRange = defaultComparisonTimePeriod
  ) => {
    return withErrorHandling(
      async () => {
        const buildRequest = (timePeriod: TimePeriodRange) =>
          ({
            ...requestBuilder.clone().setStartWeekId(timePeriod.endWeek).setEndWeekId(timePeriod.endWeek).build(),
            requestType,
            categoryType
          } as GetVariationsMetricsByCategoryRequest);

        const [mainRecord, comparisonRecord] = await Promise.all([
          client.getVariationsMetricsByCategory(buildRequest(mainTimePeriod)),
          client.getVariationsMetricsByCategory(buildRequest(comparisonTimePeriod))
        ]);

        return new VariationsByCategoryAdapter(
          categoryType,
          {
            mainRecords: mainRecord.data.records,
            comparisonRecords: comparisonRecord.data.records
          },
          { category: categoryNames, subcategory: subcategoryNames, brand: brandNames },
          requestType
        );
      },
      new VariationsByCategoryAdapter(
        categoryType,
        { mainRecords: [], comparisonRecords: [] },
        {
          category: categoryNames,
          subcategory: subcategoryNames,
          brand: brandNames
        },
        requestType
      ),
      'Error fetching variations metrics by category'
    );
  };

  const getVariationsPageSize = async (retailerId: number, mainTimePeriod: TimePeriodRange = defaultMainTimePeriod) => {
    return withErrorHandling(
      async () => {
        const response = await client.getVariationsPageSize(
          variationsRequestBuilder
            .clone()
            .setRequestType('pageSize')
            .setRetailerId(retailerId)
            .setMainTimePeriod(mainTimePeriod)
            .build()
        );

        const chartData = new VariationsPageSizeAdapter(response).transform();
        return chartData;
      },
      { chartData: [] },
      'Error fetching variations page size'
    );
  };

  const getVariationsProductDetailsGrid = async (
    retailerId: number,
    mainTimePeriod: TimePeriodRange = defaultMainTimePeriod,
    comparisonTimePeriod: TimePeriodRange = defaultComparisonTimePeriod,
    requestType: VariationsDetailGridMetricType = VariationsDetailGridMetricType.PageDetailGrid,
    variationsFilterType?: string,
    offset?: number
  ) => {
    return withErrorHandling(
      async () => {
        // Only use the last week (endWeek) of each time period for comparison
        // This matches the pattern used by other ContentTableGrid APIs
        const [mainResponse, comparisonResponse] = await Promise.all([
          client.getVariationsProductDetailsGrid(
            variationsRequestBuilder
              .clone()
              .setRequestType(requestType)
              .setRetailerId(retailerId)
              .setStartWeekId(mainTimePeriod.endWeek)
              .setEndWeekId(mainTimePeriod.endWeek)
              .setOffset(offset || 0)
              .setVariationsFilterType(variationsFilterType)
              .build()
          ),
          client.getVariationsProductDetailsGrid(
            variationsRequestBuilder
              .clone()
              .setRequestType(requestType)
              .setRetailerId(retailerId)
              .setStartWeekId(comparisonTimePeriod.endWeek)
              .setEndWeekId(comparisonTimePeriod.endWeek)
              .setOffset(offset || 0)
              .setVariationsFilterType(variationsFilterType)
              .build()
          )
        ]);

        // Extract all unique stackline SKUs from main
        const allStacklineSkus = Array.from(
          new Set([...mainResponse.data.records.map((record) => record.stacklineSku)])
        );

        // Fetch product metadata for all SKUs
        const products = await getProducts(allStacklineSkus);
        const productsMap = products.reduce((acc, product) => {
          acc[product.stacklineSku] = product;
          return acc;
        }, {} as Record<string, Product>);

        return {
          main: mainResponse,
          comparison: comparisonResponse,
          productsMap
        };
      },
      {
        main: { isSuccess: false, data: { records: [] } },
        comparison: { isSuccess: false, data: { records: [] } },
        productsMap: {} as Record<string, Product>
      },
      'Error fetching variations product details grid'
    );
  };

  return {
    getContentScoresKeyMetrics,
    getContentScoresByWeek,
    getContentScoresByProduct,
    getContentScoreByCategory,
    getProductLevelContentUtilizationDetails,
    getProductLevelContentAccuracyDetails,
    getKeywordPerformanceScoreSummary,
    getProductLevelKeywordPerformanceDetails,
    getContentScoresKeyMetricsByWeek,
    getProductLevelVoiceAndToneAnalysis,
    getProductContent,
    getProductCountByVoiceOrTone,
    getBeaconContentScoreByCategory,
    getKeywordOpportunities,
    getVariationsOverviewMetricsForGaugeCards,
    getVariationsMetrics,
    getVariationsMetricsForSplineChart,
    getVariationsByCategory,
    getVariationsPageSize,
    getVariationsProductDetailsGrid
  };
};
